import React from 'react'
import { inject, observer } from 'mobx-react'
import classnames from 'classnames'
import { withTranslation } from 'react-i18next'
import _ from 'lodash'
import { selectionTypes } from '../../constants'

import SearchIcon from '../common/icons/SearchIcon'
import PoiSearchResults from './poiSearchResults/PointSearchResults'
import PoiAutocomplete, { performQuerySearch } from './poiAutocomplete/poiAutocomplete'
import SaveOrUpgradeModal from '../common/SaveOrUpgradeModal'
import PeopleDataSidebar from '../tools/PeopleDataSidebar'
import IncomeDataSidebar from '../tools/IncomeDataSidebar'
import Sections from '../RightSideBar/Sections'
import InfoPanel from '../RightSideBar/information/InfoPanel'

const debouncedAutocompleteQuery = _.debounce((query, uiStore, poi) => {
  if (!query) {
    poi.clearPredictions()
    return
  }

  if (query.trim()) {
    uiStore.getAutoComplete(query)
  }
}, 300)

class PointOfInterest extends React.Component {
  state = {
    activeTab: 'poi'
  }

  loadPeopleData = async () => {
    const { rightSidebarStore, selectionStore } = this.props
    if (!rightSidebarStore) return

    // Preserve current view when user already switched to individual or single-polygon mode.
    if (rightSidebarStore.singlePolygonData) return
    if (rightSidebarStore.showIndividualPolygonLines && rightSidebarStore.polygonData && rightSidebarStore.polygonData.length > 0) {
      return
    }

    const selectionItem = selectionStore && selectionStore.item
    const selectedType = selectionStore && selectionStore.type
    const shapeTypes = [
      selectionTypes.GEOPOLYGON,
      selectionTypes.POINT_BUFFER,
      selectionTypes.LINE,
      selectionTypes.ISOCHRONE,
      selectionTypes.BOUNDARY
    ]
    const shapeId = selectionItem && (selectionItem.id || selectionItem.inner_id || selectionItem.remote_db_id)

    if (selectionItem && shapeId && shapeTypes.includes(selectedType)) {
      if (
        (String(rightSidebarStore.singlePolygonId) === String(shapeId) || (rightSidebarStore.singlePolygonData && String(rightSidebarStore.singlePolygonData.id) === String(shapeId))) &&
        (rightSidebarStore.hasData || rightSidebarStore.singlePolygonData || (rightSidebarStore.polygonData && rightSidebarStore.polygonData.length > 0))
      ) {
        return
      }
      await rightSidebarStore.fetchDataForSinglePolygon(shapeId, selectionItem.name)
      return
    }

    const targetGroupFromSelection = selectedType === selectionTypes.TARGET_GROUP && selectionItem
      ? selectionItem
      : null
    const targetGroupFromContext = selectionStore && selectionStore.data && selectionStore.data.group
      ? selectionStore.data.group
      : null
    const targetGroup = targetGroupFromSelection || targetGroupFromContext

    if (!targetGroup || !targetGroup.pk) return

    if (
      String(rightSidebarStore.targetGroupId) === String(targetGroup.pk) &&
      (rightSidebarStore.hasData || (rightSidebarStore.polygonData && rightSidebarStore.polygonData.length > 0))
    ) {
      return
    }

    await rightSidebarStore.fetchPopulationDataForTargetGroup(targetGroup.pk, targetGroup.name || 'Target Group')
  }

  handlePOItoggleClick = () => {
    const { uiStore } = this.props
    // const { activeTab } = this.state

    if (uiStore.activeTab === 'poi') {
      uiStore.togglePOIMenu()
      return
    }
    uiStore.setActiveTab('poi')
    // this.setState({ activeTab: 'poi' })
    uiStore.isPOIMenuExpanded = true
  }

  handlePeopleTabClick = async () => {
    const { uiStore } = this.props
    // this.setState({ activeTab: 'people' })
    uiStore.setActiveTab('people')
    uiStore.isPOIMenuExpanded = true
    await this.loadPeopleData()
  }

  handleIncomeTabClick = async () => {
    const { uiStore } = this.props
    // this.setState({ activeTab: 'income' })
    uiStore.setActiveTab('income')
    uiStore.isPOIMenuExpanded = true
    await this.loadPeopleData()
  }

  handleSearchInputChange = (event) => {
    const { uiStore, dataStore } = this.props
    const newValue = event.target.value
    const previousValue = uiStore.poiSearchQuery || ''

    if (newValue !== previousValue) {
      dataStore.poi.clearSearchResults()
      if (!newValue || !newValue.trim()) {
        uiStore.poiSearchOriginalBounds = null
        uiStore.showSearchThisAreaButton = false
        uiStore.isNearbySearchActive = false
        uiStore.searchButtonWasShown = false
        uiStore.poiSearchBoundsRectangle = null
      }
    }

    uiStore.poiSearchQuery = newValue
    debouncedAutocompleteQuery(newValue, uiStore, dataStore.poi)
  }

  handleSearch = async () => {
    const { uiStore, dataStore, selectionStore } = this.props
    const queryText = (uiStore.poiSearchQuery || '').trim()

    if (queryText) {
      await performQuerySearch(queryText, uiStore, dataStore, selectionStore)
    } else {
      await uiStore.searchNearbyPlaces()
    }
  }

  handleSearchHolderClick = async (e) => {
    const { uiStore, dataStore } = this.props

    if (e.target === e.currentTarget && !uiStore.isPOIMenuExpanded) {
      uiStore.isPOIMenuExpanded = true
      const hasActiveSearchResults = dataStore && dataStore.poi && (
        (dataStore.poi.placeResults && dataStore.poi.placeResults.length > 0) ||
        (uiStore.poiSearchQuery && uiStore.poiSearchQuery.trim().length > 0)
      )

      if (!hasActiveSearchResults) {
        await uiStore.populatePoiResultsForCurrentSelection()
      }
    }
  }

  handleSearchInputFocus = async () => {
    const { uiStore, dataStore } = this.props

    if (!uiStore.isPOIMenuExpanded) {
      uiStore.isPOIMenuExpanded = true
      const hasActiveSearchResults = dataStore && dataStore.poi && (
        (dataStore.poi.placeResults && dataStore.poi.placeResults.length > 0) ||
        (uiStore.poiSearchQuery && uiStore.poiSearchQuery.trim().length > 0)
      )

      if (!hasActiveSearchResults) {
        await uiStore.populatePoiResultsForCurrentSelection()
      }
    }
  }

  handleSearchInputKeyDown = async (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      await this.handleSearch()
    }
  }

  handleSearchThisArea = async () => {
    const { uiStore, dataStore, selectionStore } = this.props
    uiStore.showSearchThisAreaButton = false

    const queryText = (uiStore.poiSearchQuery || '').trim()
    if (queryText) {
      await performQuerySearch(queryText, uiStore, dataStore, selectionStore)
    } else if (uiStore.isNearbySearchActive) {
      await uiStore.searchNearbyPlaces()
    }
  }

  render () {
    const { uiStore, t } = this.props
    // const { activeTab } = this.state

    return (
      <div>
        {uiStore.activeTab === 'poi' && (
          <div
            className={classnames('poi__search-holder', { active: uiStore.isPOIMenuExpanded })}
            onClick={this.handleSearchHolderClick}
          >
            <input
              className='poi__search-input'
              value={uiStore.poiSearchQuery || ''}
              onChange={this.handleSearchInputChange}
              onFocus={this.handleSearchInputFocus}
              onKeyDown={this.handleSearchInputKeyDown}
              name='poi-search'
              type='search'
              placeholder={t('GLOBAL.SEARCH')}
            />
            <PoiAutocomplete />
            <button
              type='button'
              className='poi__search-btn'
              onClick={this.handleSearch}
            >
              <SearchIcon className='poi__search-icon' />
            </button>
          </div>
        )}

        {uiStore.activeTab === 'poi' && uiStore.showSearchThisAreaButton && (
          <button
            type='button'
            className='poi__search-this-area-btn'
            onClick={this.handleSearchThisArea}
          >
            <SearchIcon className='poi__search-this-area-icon' />
            <span className='poi__search-this-area-text'>Search this area</span>
          </button>
        )}

        <div className={classnames('poi', { active: uiStore.isPOIMenuExpanded })}>
          {/* <div className='poi__buttons'>
            <button
              type='button'
              className={classnames('poi__button', 'poi__button--tool', { 'poi__button--active': uiStore.activeTab === 'poi' })}
              onClick={this.handlePOItoggleClick}
            >
              <PoiTabIcon active={uiStore.activeTab === 'poi'} />
              <span className='poi__button-label'>POI</span>
            </button>
            <button
              type='button'
              className={classnames('poi__button', 'poi__button--tool', { 'poi__button--active': uiStore.activeTab === 'people' })}
              onClick={this.handlePeopleTabClick}
            >
              <PeopleIcon active={uiStore.activeTab === 'people'} />
              <span className='poi__button-label'>PEOPLE</span>
            </button>
            <button
              type='button'
              className={classnames('poi__button', 'poi__button--tool', { 'poi__button--active': uiStore.activeTab === 'income' })}
              onClick={this.handleIncomeTabClick}
            >
              <IncomeTabIcon active={uiStore.activeTab === 'income'} />
              <span className='poi__button-label'>INCOME</span>
            </button>
          </div> */}
          <Sections className='poi__buttons' handleClick={this.loadPeopleData} />

          <div className={classnames('poi__menu', { 'poi__menu--people': uiStore.activeTab === 'people' || uiStore.activeTab === 'income' || uiStore.activeTab === 'info' })}>
            {uiStore.activeTab === 'poi' &&
              (
                <>
                  <div className='poi__actions'>
                    <h3 className='poi__saved-pois-title'>{t('POI.SAVED_POIS_TITLE')}</h3>
                  </div>

                  <PoiSearchResults />
                </>
              )}
            {
              uiStore.activeTab === 'people' &&
                (
                  <PeopleDataSidebar
                    onClose={() => {
                      uiStore.isPOIMenuExpanded = false
                      uiStore.setActiveTab('poi')
                      // this.setState({ activeTab: 'poi' })
                    }}
                  />
                )
            }
            {
              uiStore.activeTab === 'income' &&
              (
                <IncomeDataSidebar
                  onClose={() => {
                    uiStore.isPOIMenuExpanded = false
                    uiStore.setActiveTab('poi')
                  // this.setState({ activeTab: 'poi' })
                  }}
                />
              )
            }{
              uiStore.activeTab === 'info' &&
              (
                <InfoPanel />
              )
            }
          </div>
        </div>
        <SaveOrUpgradeModal />
      </div>
    )
  }
}

export default withTranslation()(
  inject('uiStore', 'dataStore', 'selectionStore', 'rightSidebarStore')(observer(PointOfInterest))
)
